import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Leaf, Hand, MapPin, Sprout, ArrowRight, Snowflake, Beer, Wine, Download, Gem, Citrus } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import api, { eur, downloadFile } from "../lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import useSeo from "../hooks/useSeo";

const reviewsLd = (items = []) => {
  if (!items.length) return [];
  const avg = items.reduce((s, r) => s + Number(r.rating || 5), 0) / items.length;
  return [{ "@context": "https://schema.org", "@type": "LocalBusiness", name: "Guatemalan Spirits & Fruit Juice (BOTTY srl)", alternateName: ["Guatemalan Spirit", "Guate Spirit", "Guatemalan Liquori"], url: "https://guatemalanspirit.it/",
    address: { "@type": "PostalAddress", addressLocality: "Salerno", addressRegion: "SA", addressCountry: "IT" },
    aggregateRating: { "@type": "AggregateRating", ratingValue: avg.toFixed(1), bestRating: "5", reviewCount: String(items.length) },
    review: items.map((r) => ({ "@type": "Review", author: { "@type": "Person", name: r.name }, reviewBody: r.text, reviewRating: { "@type": "Rating", ratingValue: String(r.rating || 5), bestRating: "5" } })) }];
};
import { homeFor } from "../components/ProtectedRoute";
import BlurredPrice, { B2B_REG } from "../components/BlurredPrice";

const HERO = "https://images.unsplash.com/photo-1782201591364-39945c7f39d2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const LEMON = "https://images.pexels.com/photos/33304616/pexels-photo-33304616.jpeg?auto=compress&w=1200";
const CRAFT = "https://images.unsplash.com/photo-1649776169794-3d78d7e09089?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const INOX = "https://images.unsplash.com/photo-1567696911980-2eed69a46042?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const KEG = "https://images.unsplash.com/photo-1760282162326-0460fb0e2c1a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const DRINK = "https://images.unsplash.com/photo-1564090654281-eb1db56d15fc?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const DEFAULTS = {
  hero: { badge: "Salerno · tra Amalfi e Cilento", title: "Spirito", title_accent: "artigianale", title_suffix: "",
    subtitle: "Non siamo una multinazionale. Siamo una piccola azienda salernitana che produce cocktail, liquori e preparati per granita a regola d'arte — con rispetto per l'ambiente e per la nostra terra.",
    image: HERO, stat1_value: "19L", stat1_label: "Fusto Cornelius", stat2_value: "-85%", stat2_label: "Vetro & plastica", stat3_value: "100%", stat3_label: "Km 0 & artigianale" },
  philosophy: { eyebrow: "La nostra filosofia", title: "Piccoli per scelta, grandi nella cura", pillars: [
    { title: "Lavoro Certosino", text: "Ogni ricetta nasce a mano, con macerazioni lente e dosaggi curati uno per uno. Nessuna scorciatoia industriale." },
    { title: "Frutta 100%", text: "Granite e succhi da frutta fresca lavorata a Salerno: niente coloranti, niente aromi artificiali." },
    { title: "Materie del Territorio", text: "Limoni della Costiera, agrumi e botaniche del Cilento. Valorizziamo e promuoviamo i prodotti di zona." }] },
  products: { eyebrow: "Il nostro listino", title: "Cosa produciamo", note: "Prezzi al pubblico indicativi. Gli agenti accedono ai prezzi riservati HoReCa dalla propria area." },
  comodato: { eyebrow: "Impianti in comodato d'uso", title: "Spillatori e granitori, senza pensieri",
    text: "Diamo in comodato d'uso spillatori refrigerati in acciaio inox per i fusti Cornelius 19L e granitori professionali per i nostri preparati di frutta e agrumi surgelati. Tu servi qualità, noi pensiamo alla macchina.",
    bullets: ["Formula a canone zero legata ai consumi", "Assistenza e manutenzione incluse", "Ammortamento calcolato sul volume di fusti"], image1: KEG, image2: DRINK },
  territory: { eyebrow: "Il territorio", title: "Tra la Costiera Amalfitana e il Cilento",
    text: "Salerno è il nostro punto di partenza: due coste, un solo mare, agrumi baciati dal sole. Ogni bottiglia racconta questa terra e sostiene chi la coltiva.", image: LEMON },
  reviews: { eyebrow: "Dicono di noi", title: "Chi ci ha scelto", items: [] },
};

function mergeContent(def, over) {
  if (!over) return def;
  const out = {};
  for (const k of Object.keys(def)) {
    const dv = def[k], ov = over[k];
    if (ov == null) out[k] = dv;
    else if (Array.isArray(dv)) out[k] = ov;
    else if (typeof dv === "object") out[k] = { ...dv, ...ov };
    else out[k] = ov;
  }
  return out;
}

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08 } }),
};

const CATS = [
  { key: "fusti", label: "Fusti Cocktail 19L", icon: Beer },
  { key: "liquori", label: "Liquori in Bottiglia", icon: Wine },
  { key: "granite", label: "Granite & Preparati", icon: Snowflake },
];

function ProductCard({ p, i }) {
  const { user } = useAuth();
  const authed = !!user;
  const isB2B = p.category === "fusti" || p.category === "granite";
  const target = isB2B ? (authed ? homeFor(user?.role) : B2B_REG) : (p.category === "collezione" ? "/negozio?cat=collezione" : "/negozio");
  return (
    <motion.div
      variants={fadeUp} custom={i} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-40px" }}
      className="group bg-white rounded-2xl overflow-hidden border border-stone-200 hover:shadow-xl hover:-translate-y-1 transition-[transform,box-shadow] duration-300"
      data-testid={`product-card-${p.id}`}>
      <Link to={target} className="block aspect-[4/3] overflow-hidden bg-stone-100" aria-label={p.name} data-testid={`product-image-link-${p.id}`}>
        <img src={p.image_url} alt={p.name} loading="lazy"
             className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
      </Link>
      <div className="p-5">
        <div className="text-[0.68rem] uppercase tracking-[0.18em] font-semibold text-[#C2593F] mb-1">{p.format}</div>
        <h3 className="font-serif text-xl font-semibold text-[#0B2A45] mb-2"><Link to={`/prodotto/${p.slug}`} className="hover:text-[#0F4C81]" data-testid={`product-title-link-${p.id}`}>{p.name}</Link></h3>
        <p className="text-sm text-stone-500 leading-relaxed mb-4 min-h-[40px]">{p.description}</p>
        {SHOW_INGREDIENTS && p.ingredients && (
          <p className="text-xs text-stone-400 mb-4 leading-relaxed" data-testid={`product-ingredients-${p.id}`}>
            <span className="font-semibold text-stone-500">Ingredienti:</span> {p.ingredients}
          </p>
        )}
        <div className="flex items-end justify-between">
          <div>
            {p.category === "fusti" && authed && (<>
                  <div className="text-[0.65rem] uppercase tracking-wider text-stone-400">Costo per drink</div>
                  <div className="font-mono font-bold text-lg text-[#0F4C81]" data-testid={`drink-cost-${p.id}`}>{eur((p.agent_price || 0) / (p.portions || 130))}</div>
                  <div className="text-[0.7rem] text-stone-400 mt-0.5">1 fusto 19L ≈ {p.portions || 130} drink</div>
                </>)}
            {p.category === "granite" && authed && (<>
                  <div className="text-[0.65rem] uppercase tracking-wider text-stone-400">Prezzo operatori</div>
                  <div className="font-mono font-bold text-lg text-[#0F4C81]" data-testid={`agent-price-${p.id}`}>{eur(p.agent_price || p.public_price)}</div>
                </>)}
            {p.category === "fusti" && !authed && (
              <BlurredPrice label="Costo per drink" masked={p.drink_cost_masked || "€ X,XX"} note={`1 fusto 19L ≈ ${p.portions || 130} drink`} testId={`drink-cost-locked-${p.id}`} />
            )}
            {p.category === "granite" && !authed && (
              <BlurredPrice label="Prezzo operatori" masked={p.agent_price_masked || "€ XX,XX"} testId={`drink-cost-locked-${p.id}`} />
            )}
            {p.category !== "fusti" && p.category !== "granite" && (<>
                <div className="text-[0.65rem] uppercase tracking-wider text-stone-400">Prezzo al pubblico</div>
                <div className="font-mono font-bold text-lg text-[#0F4C81]">{eur(p.public_price)}</div>
              </>)}
          </div>
          <span className="text-xs font-medium px-3 py-1 rounded-full bg-[#FEF9C3] text-[#854D0E]">Artigianale</span>
        </div>
      </div>
    </motion.div>
  );
}

const SHOW_INGREDIENTS = false;

const MACHINE_TYPES = [
  { key: "spillatore", label: "Spillatori", text: "Impianti refrigerati inox per fusti Cornelius 19L", image: KEG },
  { key: "granitore", label: "Granitori", text: "Granitori professionali per preparati e succhi 100% frutta", image: DRINK },
];

const LINES = [
  { key: "liquori", title: "Liquori in bottiglia", text: "Limoncello, meloncello, liquirizia, mirto: macerazioni lente e agrumi della Costiera.", image: LEMON, icon: Wine, to: "/negozio", cta: "Acquista online", tab: "liquori" },
  { key: "collezione", title: "Bottiglie da collezione", text: "Riserve numerate e selezioni limitate: pezzi importanti da collezione o da regalo.", image: CRAFT, icon: Gem, to: "/negozio?cat=collezione", cta: "Scopri la Collezione", tab: null },
  { key: "fusti", title: "Cocktail in fusto 19L", text: "Spritz, Gin Tonic e signature drink pronti alla spillatura per bar, lidi e locali.", image: KEG, icon: Beer, to: "#prodotti", cta: "Per operatori", tab: "fusti" },
  { key: "granite", title: "Granite & succhi 100% frutta", text: "Preparati per granita e succhi surgelati da frutta fresca lavorata a Salerno, senza coloranti.", image: DRINK, icon: Snowflake, to: "#prodotti", cta: "Per operatori", tab: "granite" },
];

function ProductLines({ onTab }) {
  return (
    <section className="max-w-7xl mx-auto px-6 pb-8 -mt-6 lg:-mt-10" data-testid="product-lines">
      <div className="text-center mb-8">
        <div className="eyebrow text-[#C2593F] mb-2">Non solo fusti</div>
        <h2 className="font-serif font-semibold text-2xl lg:text-3xl text-[#0B2A45]">Quattro linee, un'unica cura artigianale</h2>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {LINES.map((l, i) => (
          <motion.div key={l.key} variants={fadeUp} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }}
            className="group relative rounded-2xl overflow-hidden border border-stone-200 bg-white hover:shadow-xl hover:-translate-y-1 transition-[transform,box-shadow] duration-300" data-testid={`line-${l.key}`}>
            <div className="aspect-[4/3] overflow-hidden">
              {l.to.startsWith("#")
                ? <a href={l.to} onClick={() => l.tab && onTab(l.tab)} aria-label={l.title} data-testid={`line-image-${l.key}`}><img src={l.image} alt={l.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></a>
                : <Link to={l.to} aria-label={l.title} data-testid={`line-image-${l.key}`}><img src={l.image} alt={l.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></Link>}
            </div>
            <div className="p-5">
              <div className="w-9 h-9 rounded-lg bg-[#EFF6FF] flex items-center justify-center mb-3"><l.icon className="w-5 h-5 text-[#0F4C81]" /></div>
              <h3 className="font-serif text-lg font-semibold text-[#0B2A45] mb-1">{l.title}</h3>
              <p className="text-sm text-stone-500 leading-relaxed mb-4 min-h-[60px]">{l.text}</p>
              {l.to.startsWith("#") ? (
                <a href={l.to} onClick={() => l.tab && onTab(l.tab)} className="text-sm font-semibold text-[#0F4C81] inline-flex items-center gap-1 hover:gap-2 transition-all" data-testid={`line-cta-${l.key}`}>{l.cta} <ArrowRight className="w-4 h-4" /></a>
              ) : (
                <Link to={l.to} className="text-sm font-semibold text-[#C2593F] inline-flex items-center gap-1 hover:gap-2 transition-all" data-testid={`line-cta-${l.key}`}>{l.cta} <ArrowRight className="w-4 h-4" /></Link>
              )}
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

export default function Home() {
  const { user } = useAuth();
  const authed = !!user;
  const [products, setProducts] = useState([]);
  const [content, setContent] = useState(null);
  const [tab, setTab] = useState("fusti");

  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(data)).catch(() => {});
    api.get("/content").then(({ data }) => setContent(data)).catch(() => {});
  }, []);

  const c = mergeContent(DEFAULTS, content);
  useSeo({ path: "/", title: "Guatemalan Spirit — Liquori artigianali Salerno e Costiera",
    description: "Guatemalan Spirit, liquorificio artigianale di Salerno: limoncello, meloncello, liquirizia, bottiglie da collezione, cocktail in fusto e granite di frutta.", jsonLd: reviewsLd(c.reviews?.items) });
  const hero = c.hero;
  const pillarIcons = [Hand, Citrus, Sprout];
  const pillars = (c.philosophy.pillars || []).map((p, i) => ({ icon: pillarIcons[i] || Hand, ...p }));

  return (
    <div className="paper-grain min-h-screen">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-10 items-center pt-14 pb-20 lg:pt-20 lg:pb-28">
          <motion.div variants={fadeUp} initial="hidden" animate="show">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white border border-stone-200 mb-6">
              <MapPin className="w-3.5 h-3.5 text-[#C2593F]" />
              <span className="eyebrow text-stone-600">{c.hero.badge}</span>
            </div>
            <h1 className="font-serif font-bold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#0B2A45]">
              {c.hero.title} <span className="italic text-[#C2593F]">{c.hero.title_accent}</span> {c.hero.title_suffix}
            </h1>
            <p className="mt-6 text-lg text-stone-600 leading-relaxed max-w-xl">
              {c.hero.subtitle}
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a href="#prodotti">
                <Button data-testid="hero-cta-products" className="bg-[#0F4C81] hover:bg-[#0A365C] text-white rounded-full px-7 h-12 text-base">
                  Scopri i prodotti <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </a>
              <Link to="/negozio">
                <Button data-testid="hero-cta-shop" variant="outline" className="rounded-full px-7 h-12 text-base border-stone-300 hover:bg-[#FEF9C3]">
                  <Wine className="w-4 h-4 mr-2" />Liquori & Collezione
                </Button>
              </Link>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8 }}
                      className="relative">
            <div className="rounded-[2rem] overflow-hidden shadow-2xl border-4 border-white">
              <img src={c.hero.image} alt="Aperitivo in Costiera" className="w-full h-[420px] lg:h-[560px] object-cover" />
            </div>
            <div className="absolute -bottom-5 -left-5 bg-white rounded-2xl shadow-xl px-5 py-4 border border-stone-200 hidden sm:flex items-center gap-3">
              <Leaf className="w-8 h-8 text-[#15803D]" />
              <div>
                <div className="font-semibold text-sm text-[#0B2A45]">Liquori · Fusti · Granite · Collezione</div>
                <div className="text-xs text-stone-500">Quattro linee, tutte artigianali</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <ProductLines onTab={setTab} />

      {/* PILLARS */}
      <section id="storia" className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="eyebrow text-[#C2593F] mb-3">{c.philosophy.eyebrow}</div>
          <h2 className="font-serif font-semibold text-3xl lg:text-4xl text-[#0B2A45]">{c.philosophy.title}</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {pillars.map((p, i) => (
            <motion.div key={p.title} variants={fadeUp} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }}
                        className="bg-white rounded-2xl p-8 border border-stone-200" data-testid={`pillar-${i}`}>
              <div className="w-12 h-12 rounded-xl bg-[#EFF6FF] flex items-center justify-center mb-5">
                <p.icon className="w-6 h-6 text-[#0F4C81]" />
              </div>
              <h3 className="font-serif text-xl font-semibold text-[#0B2A45] mb-2">{p.title}</h3>
              <p className="text-sm text-stone-600 leading-relaxed">{p.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* PRODUCTS */}
      <section id="prodotti" className="bg-white/60 border-y border-stone-200">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
            <div>
              <div className="eyebrow text-[#C2593F] mb-3">{c.products.eyebrow}</div>
              <h2 className="font-serif font-semibold text-3xl lg:text-4xl text-[#0B2A45]">{c.products.title}</h2>
            </div>
            <div className="max-w-md">
              <p className="text-sm text-stone-500">
                {c.products.note}
              </p>
              <div className="flex flex-wrap gap-2 mt-4">
                <Button onClick={() => downloadFile("/reports/listino.pdf", "listino_bottiglie.pdf").catch(() => toast.error("Download non riuscito"))}
                        variant="outline" data-testid="download-listino-btn" className="rounded-full border-stone-300 hover:bg-[#FEF9C3]">
                  <Download className="w-4 h-4 mr-2" />Listino bottiglie PDF
                </Button>
                {(authed && user?.role !== "customer") && (
                  <Button onClick={() => downloadFile("/reports/listino-operatori.pdf", "listino_operatori.pdf").catch(() => toast.error("Download non riuscito"))}
                          variant="outline" data-testid="download-listino-operatori-btn" className="rounded-full border-stone-300 hover:bg-[#EFF6FF]">
                    <Download className="w-4 h-4 mr-2" />Listino fusti & granite
                  </Button>
                )}
              </div>
            </div>
          </div>

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="bg-[#FEF9C3] p-1.5 rounded-full h-auto flex-wrap gap-1" data-testid="product-tabs">
              {CATS.map((c) => (
                <TabsTrigger key={c.key} value={c.key} data-testid={`tab-${c.key}`}
                             className="rounded-full px-5 py-2.5 data-[state=active]:bg-[#0F4C81] data-[state=active]:text-white text-[#854D0E]">
                  <c.icon className="w-4 h-4 mr-2" /> {c.label}
                </TabsTrigger>
              ))}
            </TabsList>
            {CATS.map((c) => (
              <TabsContent key={c.key} value={c.key} className="mt-10">
                {c.key === "fusti" && (<>
                  <div className="mb-6 grid grid-cols-3 gap-3 sm:gap-6" data-testid="fusti-stats">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="rounded-2xl bg-white border border-stone-200 px-4 py-4 text-center">
                        <div className="font-mono font-bold text-2xl text-[#0F4C81]">{hero[`stat${i}_value`]}</div>
                        <div className="text-xs text-stone-500">{hero[`stat${i}_label`]}</div>
                      </div>
                    ))}
                  </div>
                  <div className="mb-6 rounded-2xl bg-[#FEF9C3] border border-[#FDE68A] px-5 py-4 text-sm text-[#0B2A45] flex items-start gap-3" data-testid="fusti-info">
                    <Beer className="w-5 h-5 text-[#C2593F] shrink-0 mt-0.5" />
                    <span>I nostri cocktail arrivano in <b>fusti Cornelius 19L</b> (vuoto a rendere): ogni fusto rende <b>~130 drink</b> con un <b>costo per drink estremamente competitivo</b>. I fusti sono riservati agli operatori: <b>accedi</b> o <b>contatta un agente</b> per i prezzi riservati.</span>
                  </div>
                </>)}
                {c.key === "granite" && (
                  <div className="mb-6 rounded-2xl bg-[#EFF6FF] border border-[#BFDBFE] px-5 py-4 text-sm text-[#0B2A45] flex items-start gap-3" data-testid="granite-info">
                    <Snowflake className="w-5 h-5 text-[#0F4C81] shrink-0 mt-0.5" />
                    <span>I nostri preparati per granita sono <b>surgelati</b> e <b>senza conservanti</b>: 100% purea di frutta o 100% succo di agrumi del territorio.</span>
                  </div>
                )}
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {products.filter((p) => p.category === c.key).map((p, i) => (
                    <ProductCard key={p.id} p={p} i={i} />
                  ))}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      {/* CONTATTI */}
      <section id="contatti" className="max-w-7xl mx-auto px-6 py-20" data-testid="home-contact">
        <div className="bg-[#0B2A45] text-white rounded-3xl px-8 py-12 lg:px-14 grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <div className="eyebrow text-[#FDE047] mb-3">Parliamone</div>
            <h2 className="font-serif font-semibold text-3xl lg:text-4xl leading-tight">Bar, lido o ristorante? Ti seguiamo noi.</h2>
            <p className="mt-4 text-stone-200 leading-relaxed max-w-lg">Cocktail in fusto con impianto in comodato, granite 100% frutta e liquori artigianali: un referente unico a Salerno per tutta la Campania e spedizioni in Italia.</p>
          </div>
          <div className="flex flex-wrap gap-3 lg:justify-end">
            <Button asChild className="bg-[#FDE047] hover:bg-[#FACC15] text-[#0B2A45] font-semibold rounded-full px-7 h-12"><Link to="/contatti" data-testid="home-contact-cta">Contattaci <ArrowRight className="w-4 h-4 ml-2" /></Link></Button>
            <Button asChild variant="outline" className="rounded-full px-7 h-12 border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white"><Link to={authed ? homeFor(user?.role) : B2B_REG} data-testid="home-b2b-cta">Listino operatori</Link></Button>
          </div>
        </div>
      </section>


      {/* SEO */}
      <section id="liquorificio" className="max-w-7xl mx-auto px-6 py-20" data-testid="home-seo-section">
        <div className="eyebrow text-[#C2593F] mb-3">Guatemalan Spirit · Liquorificio artigianale a Salerno, tra Costiera Amalfitana e Cilento</div>
        <h2 className="font-serif font-semibold text-2xl lg:text-4xl text-[#0B2A45] max-w-3xl leading-tight">
          Guatemalan Spirit: liquori artigianali, cocktail in fusto, granite e succhi 100% frutta
        </h2>
        <p className="mt-4 text-stone-600 max-w-3xl leading-relaxed" data-testid="seo-brand">
          <strong>Guatemalan Spirits &amp; Fruit Juice</strong> — per gli amici semplicemente <em>Guatemalan Spirit</em> o <em>Guate Spirit</em> — è il liquorificio artigianale di BOTTY srl a Salerno. Produciamo liquori della Costiera e del Cilento: limoncello, meloncello, liquirizia, spritz e cocktail in fusto ready to drink, preparati per granita e bottiglie da collezione numerate.
        </p>
        <div className="mt-8 grid md:grid-cols-3 gap-8 text-stone-700 leading-relaxed">
          <div data-testid="seo-liquori">
            <h3 className="font-semibold text-[#0F4C81] mb-2">Liquorificio e vendita liquori</h3>
            <p className="text-sm">Il liquorificio Guatemalan Spirit tra Costiera Amalfitana e Costiera Cilentana produce liquori di qualità: limoncello, meloncello, liquirizia e distillati numerati. Vendita liquori artigianali online e per bar, ristoranti e HoReCa a Salerno e in tutta Italia. Bottiglie da collezione in edizione limitata e numerata.</p>
          </div>
          <div data-testid="seo-cocktail">
            <h3 className="font-semibold text-[#0F4C81] mb-2">Cocktail in fusto ready to drink</h3>
            <p className="text-sm">Spritz, Aperol spritz, Campari spritz e Amalfitano spritz in fusto Cornelius: cocktail ready to drink pronti alla spina, con impianto in comodato d'uso e vuoto a rendere ecologico. Qualità top e costanza nel bicchiere, sempre.</p>
          </div>
          <div data-testid="seo-frutta">
            <h3 className="font-semibold text-[#0F4C81] mb-2">Granite e succhi 100% frutta</h3>
            <p className="text-sm">Lavorazione di frutta fresca per preparati per granite (granita limone, granita fragola, granita melone) e succhi 100% frutta congelati e surgelati. Senza coloranti, solo frutta di stagione lavorata a Salerno.</p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
